import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';
import { planCosechaAPI } from '../../services/api';
import './BulkUploadModal.css';

const BulkUploadModal = ({ isOpen, onClose, onSuccess, temporadaId, semana }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validar que sea un archivo Excel
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(selectedFile.type)) {
        setError('Por favor seleccione un archivo Excel válido (.xlsx o .xls)');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Por favor seleccione un archivo');
      return;
    }

    if (!temporadaId || !semana) {
      setError('Temporada y semana son requeridos');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      console.log('Iniciando carga masiva...', { temporadaId, semana, fileName: file.name });
      const response = await planCosechaAPI.bulkUpload(file, temporadaId, semana);
      console.log('Respuesta recibida:', response);
      
      if (!response || !response.data) {
        throw new Error('No se recibió respuesta del servidor');
      }
      
      console.log('Estableciendo resultado:', response.data);
      setResult(response.data);
      console.log('Estado result después de setResult');
      
      // Marcar que necesitamos refrescar cuando se cierre el modal
      if (response.data.exitosas > 0) {
        console.log('Marcando para refrescar al cerrar');
        setShouldRefresh(true);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message || 'Error al procesar el archivo');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Si hubo productos exitosos, refrescar el plan antes de cerrar
    if (shouldRefresh && onSuccess) {
      console.log('Cerrando modal y refrescando plan');
      onSuccess();
    }
    
    setFile(null);
    setResult(null);
    setError(null);
    setShouldRefresh(false);
    onClose();
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  console.log('Renderizando modal, estado:', { result, error, loading, file: file?.name });

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content bulk-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Carga Masiva de Productos</h2>
            <p className="modal-subtitle">Sube un archivo Excel con productos y cantidades a planificar</p>
          </div>
          <button className="btn-icon" onClick={handleClose} type="button">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Instrucciones */}
          <div className="info-section">
            <h3>Formato del Archivo</h3>
            <p>El archivo Excel debe contener las siguientes columnas:</p>
            <ul className="format-list">
              <li><strong>Producto:</strong> Código o nombre del producto</li>
              <li><strong>Cantidad:</strong> Número entero mayor que cero</li>
            </ul>
            <div className="info-note">
              <AlertCircle size={16} />
              <span>Los productos que ya existan en el plan serán omitidos</span>
            </div>
          </div>

          {/* Selector de archivo */}
          <div className="upload-section">
            <div className="file-input-wrapper">
              <input
                type="file"
                id="bulk-file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={loading}
                className="file-input"
              />
              <label htmlFor="bulk-file" className="file-input-label">
                <FileSpreadsheet size={24} />
                <span>{file ? file.name : 'Seleccionar archivo Excel'}</span>
              </label>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="alert alert-error">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Resultado */}
          {result && (
            <div className="result-section">
              <div className="result-summary">
                <div className="summary-card success">
                  <CheckCircle size={24} />
                  <div>
                    <div className="summary-number">{result.exitosas}</div>
                    <div className="summary-label">Productos Agregados</div>
                  </div>
                </div>
                <div className="summary-card error">
                  <AlertCircle size={24} />
                  <div>
                    <div className="summary-number">{result.errores}</div>
                    <div className="summary-label">Productos con Error</div>
                  </div>
                </div>
              </div>

              {/* Detalle de errores */}
              {result.errores > 0 && (
                <div className="errors-detail">
                  <h4>Detalle de Errores</h4>
                  <div className="errors-table-wrapper">
                    <table className="errors-table">
                      <thead>
                        <tr>
                          <th>Fila</th>
                          <th>Producto</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.detalleErrores.map((err, idx) => (
                          <tr key={idx}>
                            <td>{err.fila}</td>
                            <td>{err.producto}</td>
                            <td className="error-message">{err.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detalle de éxitos (colapsable) */}
              {result.exitosas > 0 && (
                <details className="success-detail">
                  <summary>Ver productos agregados exitosamente ({result.exitosas})</summary>
                  <div className="success-table-wrapper">
                    <table className="success-table">
                      <thead>
                        <tr>
                          <th>Fila</th>
                          <th>Producto</th>
                          <th>Cantidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.detalleExitosas.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.fila}</td>
                            <td>{item.producto}</td>
                            <td>{item.cantidad}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleClose}
            disabled={loading}
          >
            {result ? 'Cerrar' : 'Cancelar'}
          </button>
          {!result && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={!file || loading}
            >
              {loading ? (
                <>
                  <div className="spinner-small" />
                  Procesando...
                </>
              ) : (
                <>
                  <Upload size={20} />
                  Cargar Archivo
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
